import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Button,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useForm } from "../../hooks";

import { PrincipalLayouts } from "../../layouts";
import { Field } from "../components/miProfile/Field";
import { addPartner } from "../helpers";
import { DateInput } from "../components/DateInput";
import { toEnglishDate } from "../../helpers/dateFormat";
import { countryList } from "../../helpers/countryList";
import { CheckboxInput } from "../components/BooleanInput";

const roleOptions = ["Admin", "Club", "LP", "Híbrido"];
const typeDniOptions = ["DNI", "Pasaporte", "NIE", "Otro documento"];

export const AddPartner = () => {
  const navigate = useNavigate();
  const [isWaiting, setIsWaiting] = useState(false);
  const { t } = useTranslation("global");
  const date = new Date();
  const options = { day: "2-digit", month: "2-digit", year: "numeric" };
  const actualDate = date.toLocaleDateString("es-ES", options);
  const [formValues, handleInputChange, reset, setValues] = useForm({
    name: "",
    surnames: "",
    email: "",
    address: "",
    country: "España",
    phone: "",
    zipCode: "",
    swift: "",
    iban: "",
    role: "Club",
    dni: "",
    typeDni: "DNI",
    registrationDate: toEnglishDate(actualDate),
    club: "",
    language: "",
    idCrm: null,
    urlDocuments: null,
    investmentType: "Club Partner",
    visibleProfile: false,
    linkedin: '',
  });

  const {
    country,
    name,
    surnames,
    email,
    phone,
    address,
    zipCode,
    swift,
    iban,
    role,
    dni,
    typeDni,
    registrationDate,
    club,
    language,
    idCrm,
    urlDocuments,
    investmentType,
    visibleProfile,
    linkedin
  } = formValues;

  const investmentTypeOptions =
    role === "Admin" || role === "LP"
      ? ["N/A"]
      : ["Deal Partner", "Club Partner"];

  const handleConfirm = async (e) => {
    e.preventDefault();
    setIsWaiting(true);
    const resp = await addPartner(
      formValues,
      t("addpartner.firstMsgSuccess"),
      t("addpartner.secondMsgSuccess"),
      t("addpartner.errorMsg"),
      t("addpartner.mailExist"),
      navigate
    );
    setIsWaiting(false);
    resp && reset();
  };

  const handleCancel = () => {
    reset();
    navigate("/partners");
  };

  const handleRoleChange = (e) => {
    const roleValue = e.target.value;
    setValues((prevValue) => {
      return { ...prevValue, role: roleValue };
    });

    const investmentTypeValue =
      roleValue === "Admin" || roleValue === "LP" ? "N/A" : "Club Partner";
    setValues((prevValue) => {
      return { ...prevValue, investmentType: investmentTypeValue };
    });
  };

  return (
    <PrincipalLayouts>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography color="primary" variant="h5">
          {t("addpartner.title")}
        </Typography>
      </Stack>
      <Divider sx={{ marginTop: 2, marginBottom: 3 }} />

      <form onSubmit={handleConfirm}>
        <Stack minHeight="calc(100vh - 290px)" gap={2}>
          <Stack direction="row" width="100%" gap={2} alignItems="flex-end">
            <Stack width="100%">
              <Typography variant="overline" color="secondary">
                {t("myprofile.role")}
              </Typography>
              <Autocomplete
                disablePortal
                options={roleOptions}
                size="small"
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" name="role" />
                )}
                value={role}
                onSelect={handleRoleChange}
                required
              />
            </Stack>
          </Stack>

          <Stack
            direction="row"
            width="100%"
            gap={2}
            alignItems="flex-end"
            sx={{ marginBottom: "15px" }}
          >
            <Stack width="100%" padding="0 5px">
              <Typography variant="overline" color="secondary">
                {t("partnerlist.investmentType")}
              </Typography>
              <Autocomplete
                disablePortal
                options={investmentTypeOptions}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    name="investmentType"
                  />
                )}
                value={investmentType}
                onSelect={handleInputChange}
                required
                disabled={role === "Admin" || role === "LP"}
              />
            </Stack>
          </Stack>

          <Stack direction="row" width="100%" gap={2}>
            <Field
              translation="myprofile"
              name={club}
              title="club"
              handleInputChange={handleInputChange}
              required={true}
            />
            <Field
              translation="partnerlist"
              name={idCrm}
              title="idCrm"
              handleInputChange={handleInputChange}
            />
          </Stack>

          <Stack direction="row" width="100%" gap={2}>
            <Field
              translation="myprofile"
              name={name}
              title="name"
              handleInputChange={handleInputChange}
              required={true}
            />
            <Field
              translation="myprofile"
              name={surnames}
              title="surnames"
              handleInputChange={handleInputChange}
              required={true}
            />
          </Stack>

          <Stack direction="row" width="100%" gap={2}>
            <Field
              translation="myprofile"
              name={email}
              title="email"
              handleInputChange={handleInputChange}
              required={true}
            />
            <Field
              translation="myprofile"
              name={phone}
              title="phone"
              handleInputChange={handleInputChange}
            />
          </Stack>

          <Stack direction="row" width="100%" gap={2}>
            <Stack width="100%">
              <Typography variant="overline" color="secondary">
                {t("myprofile.typeDni")}
              </Typography>
              <Autocomplete
                disablePortal
                options={typeDniOptions}
                size="small"
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" name="typeDni" />
                )}
                value={typeDni}
                onSelect={handleInputChange}
                required
              />
            </Stack>
          </Stack>

          <Stack direction="row" width="100%" gap={2}>
            <Field
              translation="myprofile"
              name={dni}
              title="dni"
              handleInputChange={handleInputChange}
              required={true}
            />
            <Field
              translation="myprofile"
              name={zipCode}
              title="zipCode"
              handleInputChange={handleInputChange}
            />
          </Stack>
          <Stack direction="row" width="100%" gap={2}>
            <Field
              translation="myprofile"
              name={address}
              title="address"
              handleInputChange={handleInputChange}
            />
            <Field
              translation="myprofile"
              name={linkedin}
              title="linkedin"
              handleInputChange={handleInputChange}
            />
          </Stack>

          <Stack direction="row" width="100%" gap={2}>
            <DateInput
              name={registrationDate}
              title="registrationDate"
              handleInputChange={handleInputChange}
              required={true}
            />
            <Field
              translation="myprofile"
              name={language}
              title="language"
              handleInputChange={handleInputChange}
            />
          </Stack>

          <Stack width="100%" gap={2}>
            <Typography variant="overline" color="secondary">
              {t("myprofile.country")}
            </Typography>
            <Autocomplete
              disablePortal
              options={countryList}
              size="small"
              renderInput={(params) => (
                <TextField {...params} variant="outlined" name="country" />
              )}
              value={country}
              onSelect={handleInputChange}
              required
            />
          </Stack>

          <Stack direction="row" width="100%" gap={2}>
            <Field
              translation="myprofile"
              name={swift}
              title="swift"
              handleInputChange={handleInputChange}
            />
            <Field
              translation="myprofile"
              name={iban}
              title="iban"
              handleInputChange={handleInputChange}
            />
          </Stack>

          <Stack direction="row" width="100%" gap={2}>
            <Field
              translation="myprofile"
              name={urlDocuments}
              title="urlDocuments"
              handleInputChange={handleInputChange}
            />
          </Stack>
          <Stack direction="row" width="100%" gap={2}>
            <CheckboxInput 
              translation="myprofile"
              value={visibleProfile}
              title="visibleProfile"
              handleInputChange={handleInputChange}
            />
          </Stack>
        </Stack>

        <Stack direction="row" justifyContent="end" gap={2} marginTop={3}>
          <Button
            sx={{ width: "140px" }}
            color="secondary"
            onClick={handleCancel}
            variant="outlined"
          >
            {t("myprofile.cancelButton")}
          </Button>
          <Button
            disabled={isWaiting}
            sx={{ width: "140px" }}
            type="submit"
            variant="contained"
          >
            {t("myprofile.confirmButton")}
          </Button>
        </Stack>
      </form>
    </PrincipalLayouts>
  );
};
